import { Edit3, Undo2, X, Save, AlertCircle, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'

interface GerencialEditToolbarProps {
  updateCount: number
  deleteCount: number
  errorCount: number
  selectedCount: number
  canSave: boolean
  onUndo: () => void
  onCancel: () => void
  onSave: () => void
  onDeleteSelected: () => void
}

/**
 * Sticky amber toolbar that replaces the page header while in edit mode.
 * Real-time counters keep the user oriented; Save is disabled when validation
 * errors exist (errorCount > 0).
 */
export function GerencialEditToolbar({
  updateCount,
  deleteCount,
  errorCount,
  selectedCount,
  canSave,
  onUndo,
  onCancel,
  onSave,
  onDeleteSelected,
}: GerencialEditToolbarProps) {
  const totalChanges = updateCount + deleteCount

  return (
    <div
      className="sticky top-0 z-20 -mx-6 px-6 py-3 bg-warning/10 border-b border-warning/30 backdrop-blur-sm"
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-monumenta bg-warning/20 flex items-center justify-center shrink-0">
            <Edit3 className="w-4 h-4 text-warning" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-warning truncate">
              MODO EDIÇÃO EM LOTE
            </p>
            <p className="text-xs text-white truncate">
              {totalChanges === 0 ? (
                'Edite as células diretamente · suas alterações ficam locais até "Salvar"'
              ) : (
                <>
                  {updateCount > 0 && (
                    <span>
                      {updateCount} {updateCount === 1 ? 'linha alterada' : 'linhas alteradas'}
                    </span>
                  )}
                  {updateCount > 0 && deleteCount > 0 && ' · '}
                  {deleteCount > 0 && (
                    <span className="text-error">
                      {deleteCount} {deleteCount === 1 ? 'a excluir' : 'a excluir'}
                    </span>
                  )}
                </>
              )}
              {selectedCount > 0 && ` · ${selectedCount} em seleção`}
              {errorCount > 0 && (
                <span className="inline-flex items-center gap-1 text-error ml-2">
                  <AlertCircle size={12} />
                  {errorCount} {errorCount === 1 ? 'erro' : 'erros'} de validação
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {selectedCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDeleteSelected}
              className="text-error hover:bg-error/10"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Excluir selecionados ({selectedCount})
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onUndo} disabled={updateCount === 0}>
            <Undo2 className="w-4 h-4 mr-1" />
            Desfazer
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="w-4 h-4 mr-1" />
            Cancelar edição
          </Button>
          <Button variant="primary" size="sm" onClick={onSave} disabled={!canSave}>
            <Save className="w-4 h-4 mr-1" />
            Salvar e reprocessar DRE
            {totalChanges > 0 && ` (${totalChanges})`}
          </Button>
        </div>
      </div>
    </div>
  )
}
